import React from 'react';
import PropTypes from 'prop-types';
import ResizeObserver from 'resize-observer-polyfill';
import {debounce} from 'lodash';
import {capitalize, htmlUnescape} from 'common/utils/string-util';
import CardConfig from 'common/components/card-config';
import HelptextTooltip from 'common/components/helptext-tooltip';
import SvgSymbol from 'common/components/svg-symbol';
import './index.scss';

export default class CardHeader extends React.Component {
  static propTypes = {
    customConfigOptions: PropTypes.array,
    dataManagerUrl: PropTypes.string,
    disableConfig: PropTypes.bool,
    disableDownload: PropTypes.bool,
    disableDownloadCsv: PropTypes.bool,
    disableDownloadPointsCsv: PropTypes.bool,
    disableSeparatorLine: PropTypes.bool,
    locked: PropTypes.bool,
    priorityWarnings: PropTypes.array,
    reportId: PropTypes.number,
    scheduleId: PropTypes.number,
    subtitle: PropTypes.string,
    title: PropTypes.string.isRequired,
    warnings: PropTypes.array,
  };

  static defaultProps = {
    disableConfig: false,
    disableDownload: false,
    disableDownloadCsv: false,
    disableDownloadPointsCsv: false,
    disableSeparatorLine: false,
    locked: false,
    warnings: [],
  };

  constructor(props) {
    super(props);
    this.state = {};
    this.onResize = this.onResize.bind(this);
  }

  componentDidMount() {
    this.observer = new ResizeObserver(debounce(this.onResize, 250));
    this.observer.observe(this._node);
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  render() {
    const {priorityWarnings, warnings} = this.props;
    const subtitleWrapperStyle = {
      display: this.props.subtitle ? 'inline-block' : 'none',
      marginRight: '14px',
      whiteSpace: 'nowrap',
    };
    const subtitleStyle = {
      margin: this.state.headerWrapped
        ? `0 ${this.state.dividerWidth}px 0 -${this.state.dividerWidth}px`
        : 0,
    };
    const dividerStyle = {
      visibility: this.state.headerWrapped ? 'hidden' : 'visible',
    };
    const title = htmlUnescape(this.props.title);
    const subtitle = htmlUnescape(capitalize(this.props.subtitle));
    return (
      <div
        className={'CardHeader'}
        data-report-id={this.props.reportId}
        ref={(node) => (this._node = node)}>
        <span className={'CardHeader-title'}>{title}</span>
        <span style={subtitleWrapperStyle}>
          <span className={'CardHeader-divider'} style={dividerStyle}>
            {'/'}
          </span>
          <span className={'CardHeader-subtitle'} style={subtitleStyle}>
            {subtitle}
            {this.props.locked ? <SvgSymbol name={'lock'} /> : null}
          </span>
        </span>

        {!warnings?.length ? null : (
          <span className='CardHeader-HelptextTooltip'>
            <HelptextTooltip>
              {warnings.map((warning, index) => (
                <div key={`CardHeader-warning-${index}`}>
                  <div>{warning}</div>
                  <br />
                </div>
              ))}
            </HelptextTooltip>
          </span>
        )}

        {!priorityWarnings?.length ? null : (
          <span className='CardHeader-HelptextTooltip'>
            <HelptextTooltip icon={'exclamation'}>
              {priorityWarnings.map((priorityWarning, index) => (
                <div key={`CardHeader-priorityWarning-${index}`}>
                  {priorityWarning}
                </div>
              ))}
            </HelptextTooltip>
          </span>
        )}

        {this.props.disableConfig || (
          <CardConfig
            customOptions={this.props.customConfigOptions}
            dataManagerUrl={this.props.dataManagerUrl}
            disableDownload={this.props.disableDownload}
            disableDownloadCsv={this.props.disableDownloadCsv}
            disableDownloadPointsCsv={this.props.disableDownloadPointsCsv}
            reportId={this.props.reportId}
            scheduleId={this.props.scheduleId}
          />
        )}

        {this.props.disableSeparatorLine || <hr className={'CardHeader-hr'} />}
      </div>
    );
  }

  onResize() {
    if (!this._node) {
      return;
    }

    const style = getComputedStyle(this._node);
    const headerPaddingTop = parseInt(style.paddingTop, 10);
    const headerPaddingLeft = parseInt(style.paddingLeft, 10);
    const divider = this._node.querySelector('.CardHeader-divider');
    const headerWrapped =
      divider.parentNode.offsetTop > headerPaddingTop &&
      divider.offsetLeft === headerPaddingLeft;
    const dividerWidth = divider.offsetWidth;
    this.setState({headerWrapped, dividerWidth});
  }
}
